import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import { MembershipBenefitsCollection } from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardBorderRadius, CardSize } from '@freelancer/ui/card';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { SplitButtonOption } from '@freelancer/ui/split-button';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import {
  FollowUserToastItem,
  HireMeSplitButtonOptions,
  RecommendUserToastItem,
} from 'app/hire-me/hire-me.component';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

export enum SplitButtonClickEvent {
  HIRE_ME,
  INVITE,
}

@Component({
  selector: 'app-user-profile-hire-me',
  template: `
    <ng-container *ngIf="showFullHireme$ | flAsync; else showHireMeButton">
      <ng-container *ngIf="hireMeUser$ | flAsync as hireMeUser; else loading">
        <fl-toast-alert
          i18n="Success followed user message"
          [id]="FollowUserToastItem.FOLLOW_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          You're now following {{ hireMeUser.profileUserDisplayName }}.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error following user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error target user disallowed following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR_USER_DOES_NOT_ALLOW_FOLLOWING"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          This user does not allow users to follow them.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error already following user message"
          [id]="FollowUserToastItem.FOLLOW_ERROR_ALREADY_FOLLOWING"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          You are already following this user.
        </fl-toast-alert>

        <fl-toast-alert
          [id]="FollowUserToastItem.FOLLOW_ERROR_OVER_LIMIT"
          [timeout]="5000"
          [type]="ToastAlertType.INFO"
        >
          <ng-container
            *flFeature="Feature.MEMBERSHIPS; else followLimitNoUpsell"
            i18n="Error overlimit following user message"
          >
            Your membership plan only allows
            {{ maxFollowLimit$ | flAsync }} follows. Upgrade
            <fl-link
              flTrackingLabel="UserFollowMaxLimitUpgrade"
              link="/membership"
            >
              here.
            </fl-link>
          </ng-container>
          <ng-template #followLimitNoUpsell>
            <ng-container
              i18n="
                Error overlimit following user message with no membership upsell
              "
            >
              You've reached your maximum number of follows.
            </ng-container>
          </ng-template>
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Success unfollowing user message"
          [id]="FollowUserToastItem.UNFOLLOW_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          Successfully unfollowed user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error unfollowing user message"
          [id]="FollowUserToastItem.UNFOLLOW_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error unfollowing user.
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Success recommending user message"
          [id]="RecommendUserToastItem.RECOMMEND_SUCCESS"
          [type]="ToastAlertType.SUCCESS"
        >
          You have successfully recommended
          {{ hireMeUser.profileUserDisplayName }}
        </fl-toast-alert>

        <fl-toast-alert
          i18n="Error recommending user message"
          [id]="RecommendUserToastItem.RECOMMEND_ERROR"
          [type]="ToastAlertType.ERROR"
        >
          Error recommending user.
        </fl-toast-alert>

        <fl-card
          class="DarkCard"
          [borderRadius]="CardBorderRadius.LARGE"
          [edgeToEdge]="true"
          [flHideMobile]="true"
          [size]="CardSize.SMALL"
        >
          <fl-heading
            class="CardHeading PaddedSection"
            i18n="User Profile Hire Me Component Heading"
            [color]="HeadingColor.LIGHT"
            [headingType]="HeadingType.H2"
            [size]="TextSize.SMALL"
          >
            Contact {{ hireMeUser.profileUserDisplayName }} about your job
          </fl-heading>

          <fl-bit class="PaddedSection">
            <app-hire-me
              defaultMessage="Hi {{
                hireMeUser.profileUserDisplayName
              }}, I noticed your profile and would like to offer you my project. We can discuss any details over chat."
              i18n-defaultMessage="User Profile Hire Me module default message"
              flTrackingSection="UserProfileAside"
              [hireMeUser]="hireMeUser"
              [showSplitButton]="true"
              (showToastAlert)="showToastAlert($event)"
            ></app-hire-me>
          </fl-bit>
        </fl-card>
      </ng-container>
    </ng-container>

    <ng-template #showHireMeButton>
      <fl-card
        class="DarkCard"
        [borderRadius]="CardBorderRadius.LARGE"
        [edgeToEdge]="true"
        [flHideMobile]="true"
        [size]="CardSize.SMALL"
      >
        <fl-heading
          class="CardHeading PaddedSection"
          i18n="User Profile Hire Me Component Heading"
          [color]="HeadingColor.LIGHT"
          [headingType]="HeadingType.H2"
          [size]="TextSize.SMALL"
        >
          Contact {{ profileDisplayName$ | flAsync }} about your job
        </fl-heading>

        <fl-bit class="PaddedSection">
          <app-hire-me-split-button
            *ngIf="buttonHireMeOptions$ | flAsync as options"
            [flMarginBottom]="Margin.SMALL"
            [options]="options"
            (optionSelected)="handleLoggedOutSplitButtonOptionClicked($event)"
            (primaryClicked)="
              splitButtonClick.emit(SplitButtonClickEvent.HIRE_ME)
            "
          ></app-hire-me-split-button>

          <fl-text
            *ngIf="!(isLoggedIn$ | flAsync)"
            i18n="Login to discuss hire details label"
            [color]="FontColor.LIGHT"
          >
            Log in to discuss any details over chat.
          </fl-text>
        </fl-bit>
      </fl-card>
    </ng-template>

    <ng-template #loading>
      <fl-card
        class="DarkCard"
        [borderRadius]="CardBorderRadius.LARGE"
        [edgeToEdge]="true"
        [flHideMobile]="true"
        [size]="CardSize.SMALL"
      >
        <fl-loading-text [rows]="1"></fl-loading-text>
        <fl-hr></fl-hr>
        <fl-loading-text [rows]="2"></fl-loading-text>
        <fl-loading-text [rows]="2"></fl-loading-text>
      </fl-card>
    </ng-template>
  `,
  styleUrls: ['./user-profile-hire-me.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileHireMeComponent implements OnInit {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardBorderRadius = CardBorderRadius;
  CardSize = CardSize;
  Feature = Feature;
  FollowUserToastItem = FollowUserToastItem;
  FontColor = FontColor;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  Margin = Margin;
  RecommendUserToastItem = RecommendUserToastItem;
  SplitButtonClickEvent = SplitButtonClickEvent;
  TextSize = TextSize;
  ToastAlertType = ToastAlertType;

  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() profileDisplayName$: Rx.Observable<string>;

  @Output() splitButtonClick = new EventEmitter<SplitButtonClickEvent>();

  maxFollowLimit$: Rx.Observable<number>;
  buttonHireMeOptions$: Rx.Observable<readonly SplitButtonOption[]>;

  showFullHireme$: Rx.Observable<boolean>;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private datastore: Datastore,
    private toastAlertService: ToastAlertService,
  ) {}

  ngOnInit(): void {
    this.showFullHireme$ = Rx.combineLatest([
      this.isLoggedIn$,
      this.hireMeUser$,
    ]).pipe(
      map(
        ([isLoggedIn, hireMeUser]) =>
          isLoggedIn && !hireMeUser.isDeloitteDcUser,
      ),
    );

    this.buttonHireMeOptions$ = this.profileDisplayName$.pipe(
      map(name => [
        { copy: `Hire ${name}` },
        { copy: HireMeSplitButtonOptions.INVITE },
      ]),
    );

    this.maxFollowLimit$ = this.datastore
      .document<MembershipBenefitsCollection>(
        'membershipBenefits',
        'employer_following',
      )
      .valueChanges()
      .pipe(map(benefit => benefit.value));
  }

  showToastAlert(item: FollowUserToastItem | RecommendUserToastItem): void {
    this.toastAlertService.open(item);
    this.changeDetectorRef.detectChanges();
  }

  handleLoggedOutSplitButtonOptionClicked(option: SplitButtonOption): void {
    if (option.copy === HireMeSplitButtonOptions.INVITE) {
      this.splitButtonClick.emit(SplitButtonClickEvent.INVITE);
    }
  }
}
